<template>
    <div
        id="submission-page"
        class="page-content"
        data-aos="fade-in"
        data-aos-duration="700"
        data-aos-easing="ease-in-out"
        data-aos-once="true"
    >
        <section id="header" :class="$mq">
            <div class="title">
                <img :src="data.icon" alt="logo" class="logo" />
                <div>
                    <base-text type="h2" class="subheading">
                        {{ data.subtitle }}
                    </base-text>
                    <base-text type="h1">
                        {{ data.title }}
                    </base-text>
                </div>
            </div>

            <section id="reasons" :class="$mq">
                <div
                    class="reason"
                    v-for="(reason, index) in data.reasons"
                    :key="index"
                >
                    <img class="icon" :src="reason.icon" alt="icon" />
                    <div class="content">
                        <base-text type="h3">
                            {{ reason.title }}
                        </base-text>
                        <base-text type="p">
                            {{ reason.description }}
                        </base-text>
                    </div>
                </div>

                <base-button
                    type="a"
                    href="https://submissions.imshowcase.co.uk"
                    design="secondary"
                    width="100%"
                    :disabled="!submissionsOpen || submissionsClosed"
                >
                    {{ submitButtonText }}
                </base-button>
            </section>
        </section>

        <section v-if="data.resources.length > 0" id="resources" :class="$mq">
            <base-text type="h3" class="heading">Resources</base-text>

            <div class="resources">
                <a
                    v-for="(resource, index) in data.resources"
                    :key="index"
                    class="resource"
                    :href="resource.file"
                    download
                >
                    <base-text type="p">
                        {{ resource.label }}
                    </base-text>
                </a>
            </div>
        </section>

        <section v-if="data.faqs.length > 0" id="faq" :class="$mq">
            <base-text type="h3" class="heading">FAQs</base-text>

            <div class="faqs">
                <div v-for="(faq, index) in data.faqs" :key="index" class="faq">
                    <base-text type="p">{{ faq.question }}</base-text>
                    <base-text type="p">{{ faq.answer }}</base-text>
                </div>
            </div>

            <base-button type="router-link" to="/contact" design="primary">
                Got a question?
            </base-button>
        </section>
    </div>
</template>

<script>
    // CMS DATA LOADING
    import dataLoader from '../mixins/dataLoader';
    import data from '../content/pages/submission.md';

    export default {
        name: 'submission',

        mixins: [dataLoader(data)],

        computed: {
            now() {
                return new Date();
            },

            submissionsOpen() {
                return this.now >= this.data.open;
            },

            submissionsClosed() {
                return this.now >= this.data.close;
            },

            submitButtonText() {
                if (this.submissionsOpen && !this.submissionsClosed) {
                    return 'Submit a Project';
                } else if (!this.submissionsOpen) {
                    return this.timeTillOpen;
                } else {
                    return 'Submissions are now closed.';
                }
            },

            timeTillOpen() {
                const hour = 60 * 60 * 1000;
                const day = hour * 24;

                const now = this.now.getTime();
                const open = this.data.open.getTime();
                const difference = open - now;

                const days = Math.floor(difference / day);
                const hours = Math.floor((difference % day) / hour);

                return `${days} days and ${hours} hours till submissions open.`;
            },
        },
    };
</script>

<style lang="scss" scoped>
    section#header {
        margin: 100px 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-evenly;

        .title {
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: 900px;
            margin-bottom: 100px;

            .logo {
                width: 150px;
                margin-right: $standard-space;
            }

            .subheading {
                margin-bottom: 10px;
            }
        }

        &.mobile,
        &.tablet {
            padding: 0;
            margin: 0;

            .title {
                flex-direction: column;
                text-align: center;
                margin: $standard-space 0;

                .logo {
                    margin-right: 0;
                    width: 50px;
                    margin-bottom: $standard-space-mobile;
                }
            }
        }
    }

    section#reasons {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;

        .reason {
            width: calc(50% - #{$standard-space});
            display: flex;
            align-items: center;

            &:not(:nth-last-of-type(-n + 2)) {
                margin-bottom: $standard-space;
            }

            &:nth-of-type(odd) {
                margin-right: $standard-space;
            }

            .icon {
                width: 25%;
            }

            .content {
                margin-left: 30px;

                h3 {
                    margin-bottom: 15px;
                }
            }
        }

        .button {
            margin-top: $standard-space;
        }

        &.tablet,
        &.mobile {
            padding: 0;

            .reason {
                margin-right: 0;
                width: 100%;

                &:nth-of-type(odd) {
                    flex-direction: row-reverse;
                    .content {
                        text-align: right;
                        margin-left: 0;
                        margin-right: 30px;
                    }
                }
            }
        }

        &.tablet {
            .reason {
                margin-bottom: $standard-space;
            }
        }

        &.mobile {
            .reason {
                margin-bottom: $standard-space-mobile;
            }

            .button {
                margin-top: $standard-space-mobile;
            }
        }
    }

    section#faq,
    section#resources {
        margin: 100px 0;
        display: flex;
        flex-direction: column;

        .heading,
        .faqs,
        .resources {
            margin-bottom: $standard-space;
        }

        .faq,
        .resource {
            margin-bottom: $standard-space / 2;
        }

        &.tablet,
        &.mobile {
            text-align: center;
            align-items: center;
        }

        &.mobile {
            .faqs,
            .resources {
                margin-bottom: $standard-space-mobile;
            }
        }
    }

    .resource {
        display: inline-block;
        box-shadow: 0px 5px 10px 0px rgba(0, 0, 0, 0.2);
        background: $purple;
        padding: 10px 20px;
        color: #fff;
        text-decoration: none;

        &:not(:last-of-type) {
            margin-right: $standard-space / 2;
        }

        &:hover {
            background: darken($purple, 5%);
        }

        &:focus {
            outline: 3px solid rgba($purple, 0.7);
        }
    }
</style>
